import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormControlLabel, Switch } from '@mui/material';
import { AssignmentOutlined as AssignmentOutlinedIcon } from '@mui/icons-material';
import { Card, Autocomplete, TextField, Typography, IconButton } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { get, set } from '@totalsoft/change-tracking-react';
import { emptyArray } from 'utils/constants';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { valueOrDefault } from 'utils/functions';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_ITEMS_WRITE } from 'constants/rightsCodes';

const ItemGeneralDataComponent = ({ lens, itemTypes, onSave, saving, validation, isDirty }) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_ITEMS_WRITE);

  const onPropertyTypeChanged = useCallback(
    value => {
      (lens.typeId |> set)(value?.id);
      (lens.typeCode |> set)(value?.code);
      (lens.typeName |> set)(value?.name);
    },
    [lens.typeCode, lens.typeId, lens.typeName]
  );

  return (
    <Card
      icon={AssignmentOutlinedIcon}
      iconColor='primary'
      title={t('Items.GeneralData')}
      actions={
        <IconButton
          type='save'
          tooltip={t('Items.Save')}
          color='transparent'
          fontSize='medium'
          onClick={onSave}
          disabled={!isDirty || saving || !canWrite}
        />
      }
    >
      <Grid container spacing={2} alignItems='flex-end'>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            label={t('Items.Code')}
            value={get(lens.code) || ''}
            onChange={event => set(lens.code)(event)}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            label={t('Items.Name')}
            value={get(lens.name) || ''}
            onChange={value => set(lens.name)(value)}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            label={t('Items.ShortName')}
            value={get(lens.shortName) || ''}
            onChange={value => set(lens.shortName)(value)}
            error={!isValid(validation.shortName)}
            helperText={getErrors(validation.shortName)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            id='itemsGeneralData_typeId'
            fullWidth
            isSearchable
            isClearable
            required
            label={t('Items.Type')}
            value={{ id: get(lens?.typeId), name: get(lens?.typeName) }}
            onChange={onPropertyTypeChanged}
            options={itemTypes || emptyArray}
            error={!isValid(validation.typeId)}
            helperText={getErrors(validation.typeId)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                disabled={!canWrite}
                id='Items.Enabled'
                checked={lens.enabled |> get |> valueOrDefault(false)}
                color='secondary'
                onChange={event => set(lens.enabled)(event.target.checked)}
              />
            }
            label={<Typography>{t('Items.Enabled')}</Typography>}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            fullWidth
            label={t('Items.Description')}
            value={get(lens.description) || ''}
            onChange={value => set(lens.description)(value)}
            error={!isValid(validation.description)}
            helperText={getErrors(validation.description)}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

ItemGeneralDataComponent.propTypes = {
  lens: PropTypes.object,
  itemTypes: PropTypes.array,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  validation: PropTypes.object,
  isDirty: PropTypes.bool
};

export default ItemGeneralDataComponent;
